import React, { useLayoutEffect } from 'react';
import styled, { css } from 'styled-components';
import { SolidButton, IconName, GhostButton, BreakpointSizes } from 'dbi-pearl-ui-kit';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';

const VendorAccountSetupStepFooterStyled = styled.footer`
    position: fixed;
    right: 0;
    bottom: 0;
    width: 65%;
    z-index: 1;
    background: ${(props) => props.theme.colors.background.base};
    border-top: 1px solid #d8dcdc;

    .navigationButtonWrapper {
        display: flex;
        justify-content: end;
        padding-bottom: 12px;
        margin-right: 48px;
        button {
            width: 204px;
        }
        .backButton {
            margin-right: 24px;
        }
    }

    .navigationProgress {
        width: ${(props) => props.progress * 100}%;
        background: ${(props) =>
            props.$pearlConnectFooter
                ? '#2b4344'
                : `linear-gradient(90deg, ${props.theme.colors.primary.base} 0%, #ff5680 100%)`};
        height: 5px;
    }

    ${(props) =>
        props.$pearlConnectFooter &&
        css`
            .nextButton button:not(:disabled),
            .completeButton button:not(:disabled),
            .continueStyle button:not(:disabled),
            .submitStyle button:not(:disabled) {
                background-color: #2b4344 !important;
                color: #ffffff !important;
                border-radius: 8px !important;
            }
            .nextButton button:hover:not(:disabled),
            .completeButton button:hover:not(:disabled),
            .continueStyle button:hover:not(:disabled),
            .submitStyle button:hover:not(:disabled) {
                background-color: #234a4c !important;
                color: #ffffff !important;
            }
            .nextButton button:disabled,
            .completeButton button:disabled,
            .continueStyle button:disabled,
            .submitStyle button:disabled {
                opacity: 0.5;
            }
            .backButton button:not(:disabled) {
                border-radius: 8px !important;
            }
        `}
    .navigationProgressBar {
        margin-bottom: 12px;
        background: ${(props) => props.theme.colors.background.base};
    }
    .submitStyle button,
    .continueStyle button {
        background-color: #2b4344;
        border-color: #2b4344;
        &:hover {
            background-color: #1e3031;
            border-color: #1e3031;
        }
    }
    ${(props) => props.theme.pearlBreaks.up(BreakpointSizes.MD)} {
        .submitStyle {
            button {
                width: 204px;
            }
        }
        .continueStyle {
            button {
                width: 204px;
            }
        }
    }
    ${(props) => props.theme.pearlBreaks.down(BreakpointSizes.SM)} {
        .navigationButtonWrapper {
            margin: 16px;
        }
    }
    @media (min-width: 450px) and (max-width: 500px) {
        .navigationButtonWrapper {
            margin-left: 32px;
            margin-right: 32px;
        }
    }
    ${(props) => props.theme.pearlBreaks.down(BreakpointSizes.MD)} {
        .navigationButtonWrapper {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 24;
            .backButton {
                width: 100%;
                padding-bottom: 12px;
                margin-right: 24px;
                button {
                    width: 100%;
                }
            }
            .nextButton {
                width: 100%;
                padding-bottom: 12px;
                button {
                    width: 100%;
                }
            }
            .completeButton {
                width: 100%;
                padding-bottom: 12px;
                button {
                    width: 100%;
                }
            }
            .skipButton {
                width: 100%;
                padding-bottom: 12px;
                button {
                    width: 100%;
                }
            }
            .submitStyle {
                width: 100%;
                padding-bottom: 12px;
                display: flex;
                justify-content: flex-end;
                @media (max-width: 500px) {
                    button {
                        width: 100%;
                    }
                }
            }
            .continueStyle {
                width: 100%;
                padding-bottom: 12px;
                display: flex;
                justify-content: flex-end;
                @media (max-width: 500px) {
                    button {
                        width: 100%;
                    }
                }
            }
        }
    }
`;

function renderStandardNavContent({
    isSubmit,
    isLetsGo,
    handleOnSkip,
    finalStep,
    isPay,
    handleSubmit,
    getValues,
    submitButtonText,
    t,
    rest,
    handleContinue,
}) {
    return (
        <>
            {!isSubmit && !isLetsGo && !handleOnSkip && !finalStep && !isPay && (
                <div className="nextButton">
                    <SolidButton
                        data-testid="next-button"
                        type="submit"
                        endIcon={IconName.ARROW_RIGHT}
                        iconVariant="arrow"
                        {...rest}
                    >
                        {t('Forms.Labels.Next')}
                    </SolidButton>
                </div>
            )}
            {isSubmit && (
                <div className="submitStyle">
                    <SolidButton
                        type="submit"
                        endIcon={IconName.ARROW_RIGHT}
                        onClick={() => handleSubmit(getValues())}
                    >
                        {submitButtonText || t('Forms.Labels.Submit')}
                    </SolidButton>
                </div>
            )}
            {!isSubmit && isLetsGo && (
                <div className="continueStyle">
                    <SolidButton
                        type="submit"
                        endIcon={IconName.ARROW_RIGHT}
                        onClick={() => handleContinue()}
                    >
                        {t('Forms.BasicDetails.WelcomeBan.LetsGo')}
                    </SolidButton>
                </div>
            )}
            {!handleOnSkip && finalStep && !isPay && (
                <div className="completeButton">
                    <SolidButton data-testid="complete-button" type="submit" {...rest}>
                        {t('Forms.Labels.Complete')}
                    </SolidButton>
                </div>
            )}
        </>
    );
}

function renderPayButtons({ isPay, t, rest }) {
    if (!isPay) {
        return null;
    }
    return (
        <div className="nextButton">
            <SolidButton data-testid="pay-button" type="submit" {...rest}>
                {t('Forms.StripeCheckout.PayNow')}
            </SolidButton>
        </div>
    );
}

const VendorAccountSetupStepFooter = (props) => {
    const {
        progress,
        handleOnBack,
        handleOnSkip,
        handleSubmit,
        isSubmit,
        isLetsGo,
        isPay,
        showBack,
        handleContinue,
        submitButtonText,
        pearlConnectFooter,
        ...rest
    } = props;
    const { getValues } = useFormContext();
    const { t } = useTranslation();
    const finalStep = progress === 1;

    useLayoutEffect(() => {
        if (document.querySelectorAll('.nextButton')?.length > 0) {
            document.querySelectorAll('.nextButton').forEach((div) => {
                Array.from(div.children).forEach((child, index) => {
                    if (index !== 0) {
                        child.style.display = 'none';
                    }
                });

                div.childNodes.forEach((node, index) => {
                    if (node.nodeType === Node.TEXT_NODE && index !== 0) {
                        node.remove();
                    }
                });
            });
        }
    });

    return (
        <VendorAccountSetupStepFooterStyled
            {...rest}
            $pearlConnectFooter={pearlConnectFooter}
            progress={progress}
        >
            <div className="navigationProgressBar">
                <div className="navigationProgress"></div>
            </div>
            <div className="navigationButtonWrapper">
                {showBack && (
                    <div className="backButton">
                        <SolidButton
                            data-testid="back-button"
                            startIcon={IconName.ARROW_LEFT}
                            disabled={!handleOnBack}
                            onClick={() => handleOnBack()}
                            variant="secondary"
                            iconVariant="arrow"
                        >
                            {t('Forms.Labels.Back')}
                        </SolidButton>
                    </div>
                )}
                {renderStandardNavContent({
                    isSubmit,
                    isLetsGo,
                    handleOnSkip,
                    finalStep,
                    isPay,
                    handleSubmit,
                    getValues,
                    submitButtonText,
                    t,
                    rest,
                    handleContinue,
                })}
                {renderPayButtons({
                    isPay,
                    t,
                    rest,
                })}
                {handleOnSkip && (
                    <div className="skipButton">
                        <GhostButton
                            data-testid="skip-button"
                            type="button"
                            onClick={() => handleOnSkip()}
                            endIcon={IconName.ARROW_RIGHT}
                            variant="accent"
                            iconVariant="arrow"
                        >
                            {t('Forms.Labels.Skip')}
                        </GhostButton>
                    </div>
                )}
            </div>
        </VendorAccountSetupStepFooterStyled>
    );
};

export { VendorAccountSetupStepFooter };
